import React, { useState, useMemo } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Mail, Download, ExternalLink, CheckCircle2, Printer, AlertTriangle, RefreshCw } from 'lucide-react';

const inputCls =
  'w-full bg-[#111115] border border-white/10 text-white text-xs h-8 px-3 focus:outline-none focus:border-primary/60 placeholder:text-white/20';

const PACKAGE_FORMATS = [
  { value: 'Letter', label: 'Letter' },
  { value: 'LargeLetter', label: 'Large Letter' },
  { value: 'Parcel', label: 'Parcel' },
];

export default function RoyalMailDispatchPanel({ order, onSaved }) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [marking, setMarking] = useState(false);
  // True while the admin is requesting a replacement label for an order that
  // already has a Click & Drop request.
  const [reissuing, setReissuing] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  // Pre-fill the package weight from the linked Product's stored weight.
  const { data: primaryProduct } = useQuery({
    queryKey: ['product', order.product_id],
    queryFn: () => base44.entities.Product.get(order.product_id),
    enabled: !!order.product_id,
    staleTime: 1000 * 60 * 30,
  });

  const prefillWeightGrams = useMemo(() => {
    let total = 0;
    const lineItems = Array.isArray(order.line_items) && order.line_items.length
      ? order.line_items
      : [{ qty: order.quantity || 1, product_id: order.product_id }];
    // For the simple single-product case use the fetched primary product.
    for (const li of lineItems) {
      const qty = Number(li.qty) || 1;
      const product = (li.product_id || order.product_id) === order.product_id ? primaryProduct : null;
      if (product && product.weight) {
        const v = Number(product.weight) || 0;
        const unit = product.weight_unit || 'g';
        const g = unit === 'kg' ? v * 1000 : unit === 'lb' ? v * 453.592 : unit === 'oz' ? v * 28.3495 : v;
        total += g * qty;
      }
    }
    return total > 0 ? Math.max(1, Math.round(total)) : null;
  }, [order, primaryProduct]);

  const [form, setForm] = useState({
    service: '',
    packageFormat: 'Parcel',
    weightInGrams: prefillWeightGrams || 250,
  });

  // Update the weight once the product lookup resolves.
  React.useEffect(() => {
    if (prefillWeightGrams) setForm((f) => ({ ...f, weightInGrams: prefillWeightGrams }));
  }, [prefillWeightGrams]);

  const { data: services = [], isLoading, error: svcError } = useQuery({
    queryKey: ['royal-mail-services'],
    queryFn: async () => {
      const r = await base44.functions.invoke('royalMailServices');
      const data = r?.data ?? r;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    select: (data) => data?.services || [],
    staleTime: 0,
    retry: 2,
  });

  const orderIdentifier = order.royal_mail_order_identifier;
  const created = !!orderIdentifier;
  // Dispatched = tracking number or label present (one-click OBA flow, or the
  // two-step mark-despatched completed). If created but neither, we're in the
  // OLP "print in Click & Drop then mark despatched" intermediate state.
  const fullyDispatched = created && (!!order.tracking_number || !!order.royal_mail_label_url);
  const trackingUrl = order.tracking_number
    ? `https://www.royalmail.com/track-your-item#/tracking-results/${order.tracking_number}`
    : '';

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.service) {
      setError('Select a Royal Mail service first');
      return;
    }
    const [serviceCode, serviceRegisterCode] = form.service.split('|');
    setBusy(true);
    setError('');
    setInfo('');
    try {
      const raw = await base44.functions.invoke('royalMailDispatch', {
        orderId: order.id,
        serviceCode,
        serviceRegisterCode: serviceRegisterCode || 'st',
        packageFormat: form.packageFormat,
        weightInGrams: Number(form.weightInGrams) || 100,
        // Re-requesting: always create a fresh Click & Drop order and replace
        // the stored label rather than reusing the old reference.
        reissue: created,
      });
      const res = raw?.data ?? raw;
      if (res?.error) throw new Error(res.error);
      if (res?.labelAvailable === false) {
        setInfo(res.message || 'Order created in Click & Drop. Print the label there, then mark despatched.');
      } else if (res?.warning) {
        setInfo(res.warning);
      }
      setReissuing(false);
      await queryClient.invalidateQueries({ queryKey: ['order-shipments', order.id] });
      await onSaved?.();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Dispatch failed');
    } finally {
      setBusy(false);
    }
  };

  const handleMarkDespatched = async () => {
    setMarking(true);
    setError('');
    setInfo('');
    try {
      const raw = await base44.functions.invoke('royalMailMarkDespatched', { orderId: order.id });
      const res = raw?.data ?? raw;
      if (res?.error) throw new Error(res.error);
      await queryClient.invalidateQueries({ queryKey: ['order-shipments', order.id] });
      await onSaved?.();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Could not mark despatched');
    } finally {
      setMarking(false);
    }
  };

  const cancelReissue = () => {
    setReissuing(false);
    setError('');
    setInfo('');
  };

  // The service / package / weight form — used for a first dispatch and for
  // requesting a replacement label.
  const renderForm = () => (
    <form onSubmit={handleCreate} className="space-y-2.5">
      {reissuing && (
        <div className="flex items-start gap-1.5 text-amber-400 text-[11px]">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            This creates a brand-new Click &amp; Drop order and replaces the current label
            {orderIdentifier ? <> (<span className="font-mono">#{orderIdentifier}</span>)</> : null}. The existing
            postage stays as it is in Click &amp; Drop.
          </span>
        </div>
      )}

      <div>
        <p className="text-white/30 text-[9px] font-bold tracking-widest mb-1">SERVICE</p>
        {isLoading ? (
          <div className="flex items-center gap-2 text-white/40 text-xs">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading available services…
          </div>
        ) : svcError ? (
          <div className="space-y-1">
            <p className="text-red-400 text-[10px]">
              Could not load Royal Mail services: {svcError.message || 'unknown error'}
            </p>
            <button
              type="button"
              onClick={() => queryClient.invalidateQueries({ queryKey: ['royal-mail-services'] })}
              className="text-primary hover:text-primary/80 text-[10px] font-bold tracking-wider"
            >
              RETRY
            </button>
          </div>
        ) : services.length === 0 ? (
          <p className="text-amber-400 text-[10px]">No Royal Mail services configured on this account.</p>
        ) : (
          <select
            value={form.service}
            onChange={(e) => setForm({ ...form, service: e.target.value })}
            className="w-full bg-[#111115] border border-white/10 text-white text-xs h-8 px-2 focus:outline-none focus:border-primary/60"
          >
            <option value="" className="bg-[#111115]">Select a service…</option>
            {services.map((s) => (
              <option
                key={`${s.serviceCode}|${s.serviceRegisterCode}`}
                value={`${s.serviceCode}|${s.serviceRegisterCode}`}
                className="bg-[#111115]"
              >
                {s.carrier} — {s.name}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <p className="text-white/30 text-[9px] font-bold tracking-widest mb-1">PACKAGE FORMAT</p>
          <select
            value={form.packageFormat}
            onChange={(e) => setForm({ ...form, packageFormat: e.target.value })}
            className="w-full bg-[#111115] border border-white/10 text-white text-xs h-8 px-2 focus:outline-none focus:border-primary/60"
          >
            {PACKAGE_FORMATS.map((p) => (
              <option key={p.value} value={p.value} className="bg-[#111115]">{p.label}</option>
            ))}
          </select>
        </div>
        <div>
          <p className="text-white/30 text-[9px] font-bold tracking-widest mb-1">
            WEIGHT (g) {prefillWeightGrams && <span className="text-white/20 normal-case font-normal">· from product</span>}
          </p>
          <input
            type="number"
            min="1"
            value={form.weightInGrams}
            onChange={(e) => setForm({ ...form, weightInGrams: e.target.value })}
            className={inputCls}
          />
        </div>
      </div>

      {info && <p className="text-amber-400 text-[10px]">{info}</p>}
      {error && <p className="text-red-400 text-[10px]">{error}</p>}

      <div className="flex items-center gap-3 pt-1">
        <button
          type="submit"
          disabled={busy || isLoading || !form.service}
          className="flex items-center gap-1.5 bg-primary hover:bg-primary/90 text-white text-[10px] font-bold tracking-widest px-3 py-1.5 transition-colors disabled:opacity-40"
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
          {reissuing ? 'CREATE NEW POSTAGE LABEL' : 'CREATE CLICK & DROP ORDER'}
        </button>
        {reissuing && (
          <button
            type="button"
            onClick={cancelReissue}
            className="text-white/40 hover:text-white text-[10px] font-bold tracking-widest px-1 py-1.5 transition-colors"
          >
            CANCEL
          </button>
        )}
      </div>
    </form>
  );

  const reissueButton = (
    <button
      type="button"
      onClick={() => { setReissuing(true); setError(''); setInfo(''); }}
      className="flex items-center gap-1.5 border border-primary/40 hover:bg-primary/10 text-primary text-[10px] font-bold tracking-widest px-3 py-1.5 transition-colors"
    >
      <RefreshCw className="w-3 h-3" /> CREATE NEW POSTAGE LABEL
    </button>
  );

  return (
    <div className="border border-primary/30 bg-primary/5 p-3 space-y-3">
      <div className="flex items-center gap-2">
        <Mail className="w-3.5 h-3.5 text-primary" />
        <p className="text-primary text-[10px] font-bold tracking-widest">ROYAL MAIL CLICK &amp; DROP</p>
      </div>

      {!created || reissuing ? (
        renderForm()
      ) : fullyDispatched ? (
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 text-green-400 text-[11px]">
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span>Dispatched via Royal Mail — order #{orderIdentifier}</span>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {order.tracking_number && (
              <span className="text-white/70 text-xs">
                Tracking: <span className="font-mono text-white">{order.tracking_number}</span>
                <a href={trackingUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80 ml-1.5 inline-flex">
                  <ExternalLink className="w-3 h-3" />
                </a>
              </span>
            )}
            {order.royal_mail_label_url && (
              <a
                href={order.royal_mail_label_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 bg-primary hover:bg-primary/90 text-white text-[10px] font-bold tracking-widest px-3 py-1.5 transition-colors"
              >
                <Download className="w-3 h-3" /> REPRINT LABEL
              </a>
            )}
            {reissueButton}
          </div>
          {error && <p className="text-red-400 text-[10px]">{error}</p>}
        </div>
      ) : (
        // OLP intermediate state: order created in Click & Drop, awaiting label
        // print + mark-despatched.
        <div className="space-y-2.5">
          <div className="flex items-start gap-1.5 text-amber-400 text-[11px]">
            <Printer className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>
              Order created in Click &amp; Drop — <span className="font-mono">#{orderIdentifier}</span>.
              Print the label inside Click &amp; Drop, then mark it despatched here to pull the tracking number back.
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <a
              href="https://business.parcel.royalmail.com/order"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-1.5 border border-primary/40 hover:bg-primary/10 text-primary text-[10px] font-bold tracking-widest px-3 py-1.5 transition-colors"
            >
              <ExternalLink className="w-3 h-3" /> OPEN CLICK &amp; DROP
            </a>
            {reissueButton}
          </div>
          {error && <p className="text-red-400 text-[10px]">{error}</p>}
          <button
            type="button"
            onClick={handleMarkDespatched}
            disabled={marking}
            className="flex items-center gap-1.5 bg-primary hover:bg-primary/90 text-white text-[10px] font-bold tracking-widest px-3 py-1.5 transition-colors disabled:opacity-40"
          >
            {marking ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
            MARK DESPATCHED
          </button>
        </div>
      )}
    </div>
  );
}